import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FileUploader from "@/components/shared/FileUploader";
import { Button } from "@/components/ui/button";
import { createdProduct } from "@/services/liveStreamService";
import { Loader } from "@/components/shared";
import { X, Truck, AlertTriangle, Info, Calculator, Package } from "lucide-react";
import { uploadFile, getFilePreview, getFileView, deleteFile } from "@/lib/appwrite/api";

const ProductValidation = z.object({
  title: z.string().min(2, {
    message: "Title must be at least 2 characters.",
  }),
  description: z.string().min(10, {
    message: "Description must be at least 10 characters.",
  }),
  price: z.string().refine(val => !isNaN(Number(val)) && Number(val) >= 0, {
    message: "Price must be a positive number.",
  }),
  minQuantity: z.string().refine(val => !isNaN(Number(val)) && Number(val) > 0, {
    message: "Minimum quantity must be a positive number.",
  }),
  file: z.array(z.instanceof(File)).optional(),
});

interface Product {
    $id: string;
    title: string;
    description: string;
    price: string;
    minQuantity: string;
    images: string[];
    imageIds: string[];
    sellerId: string;
    status: string;
  }
  
  interface ProductCreateModalProps {
    sellerId: string;
    onClose: () => void;
    onProductCreated?: (product: Product) => void;
  }

  const ProductCreateModal = ({ 
    sellerId, 
    onClose, 
    onProductCreated 
  }: ProductCreateModalProps) => {

  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{url: string, id: string}[]>([]);
  const [showPriceHelper, setShowPriceHelper] = useState(false);
  const [priceCalculator, setPriceCalculator] = useState({
    basePrice: '',
    packaging: '',
    shipping: '',
    margin: ''
  });

  const form = useForm<z.infer<typeof ProductValidation>>({
    resolver: zodResolver(ProductValidation),
    defaultValues: {
      title: "",
      description: "",
      price: "0",
      minQuantity: "1",
      file: [],
    },
  });

  const handleFileUpload = async (files: File[]) => {
    try {
      setIsLoading(true);
      const newUploads: {url: string, id: string}[] = [];
      
      for (const file of files) {
        try {
          const uploadedFile = await uploadFile(file);
          
          if (!uploadedFile) continue;
          const fileType = file.type;
          const fileUrl = fileType.startsWith("image")
            ? getFilePreview(uploadedFile.$id)
            : getFileView(uploadedFile.$id);
            
          if (fileUrl) {
            newUploads.push({ url: fileUrl, id: uploadedFile.$id });
          } else {
            await deleteFile(uploadedFile.$id);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }
      
      setUploadedFiles(prev => [...prev, ...newUploads]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const files = form.getValues('file');
    if (files && files.length > 0) {
      handleFileUpload(files);
      form.resetField('file');
    }
  }, [form.watch('file')]);

  // Calculate suggested price
  const calculateSuggestedPrice = () => {
    const base = parseFloat(priceCalculator.basePrice) || 0;
    const packaging = parseFloat(priceCalculator.packaging) || 0;
    const shipping = parseFloat(priceCalculator.shipping) || 0;
    const margin = parseFloat(priceCalculator.margin) || 0;
    
    const totalCost = base + packaging + shipping;
    const finalPrice = totalCost + (totalCost * margin / 100);
    
    return finalPrice;
  };

  const applySuggestedPrice = () => {
    const suggestedPrice = calculateSuggestedPrice();
    form.setValue('price', suggestedPrice.toFixed(0));
    setShowPriceHelper(false);
  };

  async function onSubmit(values: z.infer<typeof ProductValidation>) {
    try {
      setIsLoading(true);
      
      if (uploadedFiles.length === 0) {
        toast({
          title: "Warning",
          description: "Please upload at least one image for your product",
          variant: "destructive",
        });
        return;
      }

      const imageUrls = uploadedFiles.map(file => file.url);
      const imageIds = uploadedFiles.map(file => file.id);
      const productData = {
        title: values.title,
        description: values.description,
        price: values.price,
        minQuantity: values.minQuantity,
        images: imageUrls,
        imageIds: imageIds,
        sellerId: sellerId,
        status: "active"
      };

      const result = await createdProduct(productData);
      
      if (result) {
        toast({
          title: "Product Created! 🎉",
          description: "Your product is now ready to be featured in live streams.",
        });
        if (onProductCreated) {
            onProductCreated({
                ...result,
                $id: result.id || Date.now().toString(),
                status: "active",
                minQuantity: values.minQuantity,
                imageIds: []
            });
          }
        onClose();
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast({
        title: "Error",
        description: "Failed to create product. Please try again.",
        variant: "destructive",
      });
      
      try {
        await Promise.all(uploadedFiles.map(file => deleteFile(file.id)));
      } catch (cleanupError) {
        console.error("Error cleaning up files:", cleanupError);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleRemoveFile = async (id: string) => {
    try {
      await deleteFile(id);
      setUploadedFiles(prev => prev.filter(file => file.id !== id));
    } catch (error) {
      console.error("Error removing file:", error);
      toast({
        title: "Error",
        description: "Failed to remove image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto" 
      onClick={onClose}
    >
      <div 
        className="bg-dark-2 rounded-lg p-4 md:p-6 w-full max-w-md md:max-w-lg lg:max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="h3-bold md:h2-bold text-light-1">Create New Product</h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            disabled={isLoading}
            className="hover:bg-dark-4"
          >
            <X className="h-5 w-5 text-light-1" />
          </Button>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="shad-form_label">Product Title</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter product title" 
                      className="shad-input" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage className="shad-form_message" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="shad-form_label">Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your product in detail"
                      className="shad-textarea custom-scrollbar"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="shad-form_message" />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="shad-form_label flex items-center justify-between">
                      <span>Price (Rs.)</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPriceHelper(!showPriceHelper)}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        <Calculator size={12} className="mr-1" />
                        Price Helper
                      </Button>
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.01" 
                        min="0" 
                        placeholder="0.00" 
                        className="shad-input" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage className="shad-form_message" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="minQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="shad-form_label">Min Quantity</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1" 
                        placeholder="1" 
                        className="shad-input" 
                        {...field} 
                      />
                    </FormControl>
                    <p className="text-light-4 text-xs mt-1">
                      Minimum units buyers must purchase (for bulk orders)
                    </p>
                    <FormMessage className="shad-form_message" />
                  </FormItem>
                )}
              />
            </div>

            {/* Price Helper Calculator */}
            {showPriceHelper && (
              <div className="bg-blue-50 border text-gray-700 border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Calculator className="w-4 h-4 mr-2 text-blue-600" />
                  <h4 className="text-sm font-semibold text-blue-800">Smart Price Calculator</h4>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs font-medium text-blue-700 block mb-1">
                      Product Cost (₹)
                    </label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={priceCalculator.basePrice}
                      onChange={(e) => setPriceCalculator(prev => ({ ...prev, basePrice: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-blue-700 block mb-1">
                      Packaging (₹)
                    </label>
                    <Input
                      type="number"
                      placeholder="20"
                      value={priceCalculator.packaging}
                      onChange={(e) => setPriceCalculator(prev => ({ ...prev, packaging: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-blue-700 block mb-1">
                      Shipping (₹)
                    </label>
                    <Input
                      type="number"
                      placeholder="50"
                      value={priceCalculator.shipping}
                      onChange={(e) => setPriceCalculator(prev => ({ ...prev, shipping: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-blue-700 block mb-1">
                      Profit Margin (%)
                    </label>
                    <Input
                      type="number"
                      placeholder="20"
                      value={priceCalculator.margin}
                      onChange={(e) => setPriceCalculator(prev => ({ ...prev, margin: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 border border-blue-300">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">Suggested Price:</span>
                    <span className="text-lg font-bold text-green-600">
                      ₹{calculateSuggestedPrice().toFixed(0)}
                    </span>
                  </div>
                  <Button
                    type="button"
                    onClick={applySuggestedPrice}
                    className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    size="sm"
                  >
                    Use This Price
                  </Button>
                </div>
              </div>
            )}

            {/* Delivery Information */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <Truck size={16} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-orange-800 mb-2">
                    📦 Delivery Responsibility
                  </h4>
                  <div className="space-y-2 text-sm text-orange-700">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>You handle all deliveries:</strong> Include packaging, shipping, and handling costs in your price.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Package size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>Packaging tip:</strong> Use sturdy packaging to ensure products reach customers safely.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Info size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>Communication:</strong> Share tracking details with customers after dispatch.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="file"
              render={({ field }) => (
                <FormItem className="md:w-1/2 w-full">
                  <FormLabel className="shad-form_label">Product Images</FormLabel>
                  <FormControl>
                    <>
                      <FileUploader 
                        fieldChange={field.onChange}
                        mediaUrl={uploadedFiles.map(file => file.url)}
                        mode="post"
                        maxFiles={6}
                      />
                      {uploadedFiles.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          {uploadedFiles.map((file) => (
                            <div key={file.id} className="relative group">
                              <img 
                                src={file.url} 
                                alt="Product preview" 
                                className="w-full h-24 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(file.id)}
                                className="absolute top-1 right-1 bg-dark-4/80 text-light-1 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  </FormControl>
                  <p className="text-light-4 small-regular mt-2">
                    Upload up to 6 high-quality product images
                  </p>
                  <FormMessage className="shad-form_message" />
                </FormItem>
              )}
            />

            <div className="flex gap-4 mb-6 items-center justify-end mt-6">
              <Button
                type="button"
                className="shad-button_dark_4"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="shad-button_primary whitespace-nowrap"
                disabled={isLoading}
              >
                {isLoading && <Loader />}
                Create Product
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ProductCreateModal;